import { App, View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMLevelCard,
  PMLineGaugeCard,
} from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import {
  calculateMaturityGauge,
  getContrastColor,
  getCurrentMaturityIndexForItems,
} from "product-test-utils";

import { TestAppState } from "../../../app";
import { theme } from "../../../theme";

export function addMaturityCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const language = app.getAppState().language || "en";

  const projectMaturityBadgeCard = view.addComponent({
    component: PMLevelCard,
    layoutProps: {
      componentId: "projectMaturityBadgeCard",
      labels: ["maturity", "badge", "project"],
      title:
        getLocalizedMessage(language, "cards.oeMaturityLevel.title") ||
        "OE Maturity Level",
      description:
        getLocalizedMessage(language, "cards.oeMaturityLevel.description") ||
        "Shows the achieved maturity level based on completed activities.",
    },
  });

  projectMaturityBadgeCard.mapLayoutProps(({ language }) => {
    const currentLanguage = language || "en";
    return {
      componentId: "projectMaturityBadgeCard",
      labels: ["maturity", "badge", "project"],
      title:
        getLocalizedMessage(currentLanguage, "cards.oeMaturityLevel.title") ||
        "OE Maturity Level",
      description:
        getLocalizedMessage(
          currentLanguage,
          "cards.oeMaturityLevel.description"
        ) || "Shows the achieved maturity level based on completed activities.",
    };
  });

  projectMaturityBadgeCard.mapState(({ currentReport, settings, language }) => {
    const reportingItems = currentReport?.project?.reportingItems ?? [];
    const currentMaturityValue = getCurrentMaturityIndexForItems(
      reportingItems,
      settings?.configuration
    );
    const orderedMaturityEntries = (
      settings?.configuration?.maturityConfiguration ?? []
    )
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const maturityColor =
      currentMaturityValue >= 1
        ? orderedMaturityEntries[orderedMaturityEntries.length - 1]?.color
        : orderedMaturityEntries[0]?.color;
    const resolvedMaturityColor =
      maturityColor ?? theme.palette.secondary.light;

    const maturityLabel = Number.isInteger(currentMaturityValue)
      ? String(currentMaturityValue)
      : currentMaturityValue.toFixed(2);

    return {
      title:
        getLocalizedMessage(language, "cards.oeMaturityLevel.title") ||
        "OE Maturity Level",
      categories: [
        {
          value: maturityLabel,
          valueColor: resolvedMaturityColor,
          textColor: getContrastColor(resolvedMaturityColor),
        },
      ],
      showInfoIcon: false,
      showMenuIcon: false,
    };
  });

  projectMaturityBadgeCard.mapVisibility(({ settings }) => {
    return settings?.configuration?.useCase === "maturity";
  });

  const reportMaturityLevelComponent = view.addComponent({
    component: PMLineGaugeCard,
    layoutProps: {
      componentId: "reportMaturityLevelComponent",
      labels: ["maturity", "dimension", "gauge"],
      title:
        getLocalizedMessage(language, "cards.dimensionMaturityLevel.title") ||
        "Dimension Maturity Levels",
      description:
        getLocalizedMessage(
          language,
          "cards.dimensionMaturityLevel.description"
        ) ||
        "Shows numeric maturity progress for each dimension and the average across them.",
    },
  });

  reportMaturityLevelComponent.mapLayoutProps(({ language }) => {
    const currentLanguage = language || "en";
    return {
      componentId: "reportMaturityLevelComponent",
      labels: ["maturity", "dimension", "gauge"],
      title:
        getLocalizedMessage(
          currentLanguage,
          "cards.dimensionMaturityLevel.title"
        ) || "Dimension Maturity Levels",
      description:
        getLocalizedMessage(
          currentLanguage,
          "cards.dimensionMaturityLevel.description"
        ) ||
        "Shows numeric maturity progress for each dimension and the average across them.",
    };
  });

  reportMaturityLevelComponent.mapState(
    ({ currentReport, language, settings }) => {
      const maturityItems = currentReport?.project?.reportingItems ?? [];
      const gaugeState = calculateMaturityGauge({
        items: maturityItems,
        configuration: settings?.configuration,
      });

      return {
        title:
          getLocalizedMessage(language, "cards.dimensionMaturityLevel.title") ||
          "Dimension Maturity Levels",
        icon: "ShowChart",
        entries: gaugeState
          ? [
              {
                label: getLocalizedMessage(language, "average") || "Average",
                value: gaugeState.averageValue,
                percentage: gaugeState.averagePercentage,
                precision: 3,
                isTitle: true,
                color: gaugeState.averageColor,
              },
              ...gaugeState.dimensions.map((dimension) => ({
                label: dimension.name,
                value: dimension.value,
                percentage: dimension.percentage,
                precision: 2,
                color: dimension.color,
              })),
            ]
          : [],
        scale: gaugeState?.scale ?? [1],
        showInfoIcon: false,
        showMenuIcon: false,
      };
    }
  );

  reportMaturityLevelComponent.mapVisibility(({ settings }) => {
    return settings?.configuration?.useCase === "maturity";
  });
}
